import { useState } from 'react'
import { useSchoolContractors, type SchoolContractor } from '@/hooks/useSchoolContractors'
import { useSchoolPackages } from '@/hooks/useSchoolPackages'
import { useSelectedSchool } from '@/contexts/SelectedSchoolContext'
import { SchoolSwitcher } from '@/components/school/SchoolSwitcher'

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' })
}

function money(n: number | null) {
  return n === null ? '—' : `$${n.toFixed(2)}`
}

const fieldLabelStyle: React.CSSProperties = {
  font: '500 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.45)',
  textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6,
}

function AddContractorForm({ schoolId, onAdded }: { schoolId: string; onAdded: () => void }) {
  const { saving, createContractor } = useSchoolContractors(schoolId)
  const [open, setOpen] = useState(false)
  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [address, setAddress] = useState('')
  const [instructorLicenceNumber, setInstructorLicenceNumber] = useState('')
  const [instructorLicenceExpiryDate, setInstructorLicenceExpiryDate] = useState('')
  const [driverLicenceNumber, setDriverLicenceNumber] = useState('')
  const [driverLicenceExpiryDate, setDriverLicenceExpiryDate] = useState('')
  const [vehicleMake, setVehicleMake] = useState('')
  const [vehicleModel, setVehicleModel] = useState('')
  const [vehicleYear, setVehicleYear] = useState('')
  const [vehiclePlate, setVehiclePlate] = useState('')
  const [serviceAreas, setServiceAreas] = useState('')
  const [hourlyRate, setHourlyRate] = useState('')
  const [notes, setNotes] = useState('')
  const [formError, setFormError] = useState('')

  function reset() {
    setFullName(''); setPhone(''); setEmail(''); setAddress('')
    setInstructorLicenceNumber(''); setInstructorLicenceExpiryDate('')
    setDriverLicenceNumber(''); setDriverLicenceExpiryDate('')
    setVehicleMake(''); setVehicleModel(''); setVehicleYear(''); setVehiclePlate('')
    setServiceAreas(''); setHourlyRate(''); setNotes('')
  }

  async function handleSubmit() {
    setFormError('')
    if (!fullName.trim()) { setFormError('Full name is required.'); return }

    const result = await createContractor({
      fullName: fullName.trim(),
      phone: phone.trim() || null,
      email: email.trim() || null,
      address: address.trim() || null,
      instructorLicenceNumber: instructorLicenceNumber.trim() || null,
      instructorLicenceExpiryDate: instructorLicenceExpiryDate || null,
      driverLicenceNumber: driverLicenceNumber.trim() || null,
      driverLicenceExpiryDate: driverLicenceExpiryDate || null,
      vehicleMake: vehicleMake.trim() || null,
      vehicleModel: vehicleModel.trim() || null,
      vehicleYear: vehicleYear ? Number(vehicleYear) : null,
      vehiclePlate: vehiclePlate.trim() || null,
      serviceAreas: serviceAreas.split(',').map(s => s.trim()).filter(Boolean),
      hourlyRate: hourlyRate ? Number(hourlyRate) : null,
      notes: notes.trim() || null,
    })
    if (!result.ok) { setFormError('Could not add this contractor.'); return }
    reset()
    setOpen(false)
    onAdded()
  }

  return (
    <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '22px 22px', marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: open ? 18 : 0 }}>
        <div style={{ font: 'var(--text-section)', color: 'var(--ink)' }}>Add a contractor</div>
        <button
          onClick={() => setOpen(v => !v)}
          style={{
            background: open ? 'rgba(27,42,74,0.06)' : '#0F7C6C', color: open ? '#1B2A4A' : '#fff',
            border: 'none', borderRadius: 8, padding: '9px 16px', font: '600 13px Inter, sans-serif', cursor: 'pointer',
          }}
        >
          {open ? 'Cancel' : '+ Add a contractor'}
        </button>
      </div>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
            This is a private record for your own bookkeeping — it never invites this person
            onto DriveTrack or creates an account for them. If they later sign up and request
            to affiliate with your school as a contractor, that's a separate, unrelated step.
          </div>

          <div className='fg2'>
            <div>
              <div style={fieldLabelStyle}>Full name</div>
              <input value={fullName} onChange={e => setFullName(e.target.value)} className='field-input' disabled={saving} />
            </div>
            <div>
              <div style={fieldLabelStyle}>Pay per hour</div>
              <input type='number' min='0' step='0.01' value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} placeholder='35.00' className='field-input' disabled={saving} />
            </div>
          </div>

          <div className='fg2'>
            <div>
              <div style={fieldLabelStyle}>Phone</div>
              <input value={phone} onChange={e => setPhone(e.target.value)} className='field-input' disabled={saving} />
            </div>
            <div>
              <div style={fieldLabelStyle}>Email</div>
              <input type='email' value={email} onChange={e => setEmail(e.target.value)} className='field-input' disabled={saving} />
            </div>
          </div>

          <div>
            <div style={fieldLabelStyle}>Address</div>
            <input value={address} onChange={e => setAddress(e.target.value)} className='field-input' disabled={saving} />
          </div>

          <div className='fg2'>
            <div>
              <div style={fieldLabelStyle}>DI (driving instructor) licence number</div>
              <input value={instructorLicenceNumber} onChange={e => setInstructorLicenceNumber(e.target.value)} className='field-input' disabled={saving} />
            </div>
            <div>
              <div style={fieldLabelStyle}>DI licence expiry</div>
              <input type='date' value={instructorLicenceExpiryDate} onChange={e => setInstructorLicenceExpiryDate(e.target.value)} className='field-input' disabled={saving} />
            </div>
          </div>

          <div className='fg2'>
            <div>
              <div style={fieldLabelStyle}>Driver's licence number</div>
              <input value={driverLicenceNumber} onChange={e => setDriverLicenceNumber(e.target.value)} className='field-input' disabled={saving} />
            </div>
            <div>
              <div style={fieldLabelStyle}>Driver's licence expiry</div>
              <input type='date' value={driverLicenceExpiryDate} onChange={e => setDriverLicenceExpiryDate(e.target.value)} className='field-input' disabled={saving} />
            </div>
          </div>

          <div className='fg3'>
            <div>
              <div style={fieldLabelStyle}>Vehicle make</div>
              <input value={vehicleMake} onChange={e => setVehicleMake(e.target.value)} className='field-input' disabled={saving} />
            </div>
            <div>
              <div style={fieldLabelStyle}>Vehicle model</div>
              <input value={vehicleModel} onChange={e => setVehicleModel(e.target.value)} className='field-input' disabled={saving} />
            </div>
            <div>
              <div style={fieldLabelStyle}>Vehicle year</div>
              <input type='number' min='1980' max='2100' value={vehicleYear} onChange={e => setVehicleYear(e.target.value)} className='field-input' disabled={saving} />
            </div>
          </div>

          <div className='fg2'>
            <div>
              <div style={fieldLabelStyle}>Licence plate</div>
              <input value={vehiclePlate} onChange={e => setVehiclePlate(e.target.value)} className='field-input' disabled={saving} />
            </div>
            <div>
              <div style={fieldLabelStyle}>Service areas (comma-separated)</div>
              <input value={serviceAreas} onChange={e => setServiceAreas(e.target.value)} placeholder='Markham, Richmond Hill' className='field-input' disabled={saving} />
            </div>
          </div>

          <div>
            <div style={fieldLabelStyle}>Notes</div>
            <input value={notes} onChange={e => setNotes(e.target.value)} className='field-input' disabled={saving} />
          </div>

          {formError && <div className='banner-error'>{formError}</div>}

          <button onClick={handleSubmit} disabled={saving} className='btn-teal' style={{ width: 'auto', padding: '11px 22px', alignSelf: 'flex-start' }}>
            {saving ? 'Saving…' : 'Add contractor'}
          </button>
        </div>
      )}
    </div>
  )
}

function AddAssignmentForm({ schoolId, contractor, onAdded, onCancel }: {
  schoolId: string; contractor: SchoolContractor; onAdded: () => void; onCancel: () => void
}) {
  const { saving, createAssignment } = useSchoolContractors(schoolId)
  const { packages } = useSchoolPackages(schoolId)
  const activePackages = packages.filter(p => p.isActive)

  const [studentName, setStudentName] = useState('')
  const [packageId, setPackageId] = useState('')
  const [hourlyRate, setHourlyRate] = useState(contractor.hourlyRate !== null ? String(contractor.hourlyRate) : '')
  const [startedAt, setStartedAt] = useState(new Date().toISOString().slice(0, 10))
  const [formError, setFormError] = useState('')

  async function handleSubmit() {
    setFormError('')
    if (!studentName.trim()) { setFormError('Student name is required.'); return }

    const result = await createAssignment({
      contractorId: contractor.id,
      packageId: packageId || null,
      studentName: studentName.trim(),
      hourlyRateSnapshot: hourlyRate ? Number(hourlyRate) : null,
      startedAt,
    })
    if (!result.ok) { setFormError('Could not record this assignment.'); return }
    onAdded()
  }

  return (
    <div style={{ background: 'rgba(27,42,74,0.03)', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 10, padding: 16, marginTop: 10, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className='fg2'>
        <div>
          <div style={fieldLabelStyle}>Student name</div>
          <input value={studentName} onChange={e => setStudentName(e.target.value)} className='field-input' disabled={saving} />
        </div>
        <div>
          <div style={fieldLabelStyle}>Package</div>
          <select value={packageId} onChange={e => setPackageId(e.target.value)} className='field-input' disabled={saving || activePackages.length === 0}>
            <option value=''>{activePackages.length === 0 ? 'No packages available' : 'Select a package…'}</option>
            {activePackages.map(p => (
              <option key={p.id} value={p.id}>{p.name} — {p.inCarHours}h{p.price != null ? ` · $${p.price.toFixed(2)}` : ''}</option>
            ))}
          </select>
        </div>
      </div>
      <div className='fg2'>
        <div>
          <div style={fieldLabelStyle}>Pay per hour for this assignment</div>
          <input type='number' min='0' step='0.01' value={hourlyRate} onChange={e => setHourlyRate(e.target.value)} className='field-input' disabled={saving} />
        </div>
        <div>
          <div style={fieldLabelStyle}>Started</div>
          <input type='date' value={startedAt} onChange={e => setStartedAt(e.target.value)} className='field-input' disabled={saving} />
        </div>
      </div>
      {formError && <div className='banner-error'>{formError}</div>}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={handleSubmit} disabled={saving} className='btn-teal' style={{ width: 'auto', padding: '9px 18px' }}>
          {saving ? 'Saving…' : 'Record assignment'}
        </button>
        <button
          onClick={onCancel}
          style={{ background: 'none', border: 'none', font: '600 13px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', cursor: 'pointer' }}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function ContractorCard({ schoolId, contractor, assignments, onChanged }: {
  schoolId: string
  contractor: SchoolContractor
  assignments: ReturnType<typeof useSchoolContractors>['assignments']
  onChanged: () => void
}) {
  const { setContractorActive, endAssignment } = useSchoolContractors(schoolId)
  const [expanded, setExpanded] = useState(false)
  const [addingAssignment, setAddingAssignment] = useState(false)

  const ownAssignments = assignments.filter(a => a.contractorId === contractor.id)
  const vehicle = [contractor.vehicleYear, contractor.vehicleMake, contractor.vehicleModel].filter(Boolean).join(' ')

  return (
    <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '18px 20px', marginBottom: 14, opacity: contractor.isActive ? 1 : 0.6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
        <div>
          <div style={{ font: '600 15px Inter, sans-serif', color: '#1B2A4A' }}>
            {contractor.fullName}{!contractor.isActive && <span style={{ font: '500 11px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', marginLeft: 8 }}>(archived)</span>}
          </div>
          <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginTop: 3 }}>
            {[contractor.phone, contractor.email].filter(Boolean).join(' · ') || '—'}
          </div>
          {contractor.serviceAreas.length > 0 && (
            <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 3 }}>
              Serves: {contractor.serviceAreas.join(', ')}
            </div>
          )}
          {vehicle && (
            <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 3 }}>
              {vehicle}{contractor.vehiclePlate ? ` · ${contractor.vehiclePlate}` : ''}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ font: '600 15px Inter, sans-serif', color: '#0F7C6C' }}>
            {money(contractor.hourlyRate)}/hr
          </div>
          <button
            onClick={() => setContractorActive(contractor.id, !contractor.isActive).then(onChanged)}
            style={{ background: 'none', border: 'none', font: '600 12px Inter, sans-serif', color: 'rgba(27,42,74,0.45)', cursor: 'pointer', marginTop: 8 }}
          >
            {contractor.isActive ? 'Archive' : 'Reactivate'}
          </button>
        </div>
      </div>

      <div style={{ marginTop: 12, display: 'flex', gap: 14, alignItems: 'center' }}>
        <button
          onClick={() => setExpanded(v => !v)}
          style={{ background: 'none', border: 'none', font: '600 12.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', padding: 0 }}
        >
          {expanded ? 'Hide' : 'Show'} assignments ({ownAssignments.length})
        </button>
        {expanded && !addingAssignment && (
          <button
            onClick={() => setAddingAssignment(true)}
            style={{ background: 'none', border: 'none', font: '600 12.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', padding: 0 }}
          >
            + Add assignment
          </button>
        )}
      </div>

      {expanded && (
        <div style={{ marginTop: 10 }}>
          {addingAssignment && (
            <AddAssignmentForm
              schoolId={schoolId}
              contractor={contractor}
              onAdded={() => { setAddingAssignment(false); onChanged() }}
              onCancel={() => setAddingAssignment(false)}
            />
          )}

          {ownAssignments.length === 0 && !addingAssignment && (
            <div style={{ font: '400 12.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '8px 0' }}>
              No assignments recorded yet.
            </div>
          )}

          {ownAssignments.map(a => {
            const cost = a.hourlyRateSnapshot !== null && a.packageInCarHours !== null
              ? a.hourlyRateSnapshot * a.packageInCarHours
              : null
            const revenue = a.packagePrice !== null && cost !== null ? a.packagePrice - cost : null
            return (
              <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderTop: '1px solid rgba(27,42,74,0.06)' }}>
                <div>
                  <div style={{ font: '600 13px Inter, sans-serif', color: '#1B2A4A' }}>{a.studentName}</div>
                  <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)', marginTop: 2 }}>
                    {a.packageName ?? 'No package'} · started {formatDate(a.startedAt)}{a.endedAt ? ` · ended ${formatDate(a.endedAt)}` : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ font: '400 12px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
                    Package {money(a.packagePrice)} − pay {money(cost)}
                  </div>
                  <div style={{ font: '700 13px Inter, sans-serif', color: '#0F7C6C' }}>
                    {money(revenue)} revenue
                  </div>
                  {!a.endedAt && (
                    <button
                      onClick={() => endAssignment(a.id).then(onChanged)}
                      style={{ background: 'none', border: 'none', font: '600 11.5px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', cursor: 'pointer', marginTop: 4 }}
                    >
                      End
                    </button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default function Contractors() {
  const { selectedSchoolId } = useSelectedSchool()
  const { contractors, assignments, loading, error, refresh } = useSchoolContractors(selectedSchoolId)

  return (
    <div>
      <div style={{ font: '600 26px Lora, serif', color: '#1B2A4A', marginBottom: 6 }}>
        Contractors
      </div>
      <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.55)', marginBottom: 6 }}>
        Account-less contractors you pay directly, and the students/packages billed through
        them — for your own commission bookkeeping only, not for in-app lesson tracking.
      </div>

      <SchoolSwitcher />

      {selectedSchoolId && <AddContractorForm schoolId={selectedSchoolId} onAdded={refresh} />}

      {loading && (
        <div style={{ font: '400 14px Inter, sans-serif', color: 'rgba(27,42,74,0.4)', padding: '24px 0' }}>
          Loading…
        </div>
      )}

      {error && !loading && (
        <div className='banner-error' style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
          <span>{error}</span>
          <button
            onClick={refresh}
            style={{ background: 'none', border: 'none', font: '600 12.5px Inter, sans-serif', color: '#0F7C6C', cursor: 'pointer', flexShrink: 0 }}
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && contractors.length === 0 && (
        <div style={{ background: '#fff', border: '1px solid rgba(27,42,74,0.08)', borderRadius: 14, padding: '36px 28px', textAlign: 'center' }}>
          <div style={{ font: '600 16px Lora, serif', color: '#1B2A4A', marginBottom: 8 }}>
            No contractors yet
          </div>
          <div style={{ font: '400 13.5px Inter, sans-serif', color: 'rgba(27,42,74,0.5)' }}>
            Add a contractor above to start tracking what you pay them.
          </div>
        </div>
      )}

      {!loading && !error && selectedSchoolId && contractors.map(c => (
        <ContractorCard key={c.id} schoolId={selectedSchoolId} contractor={c} assignments={assignments} onChanged={refresh} />
      ))}
    </div>
  )
}
